import { SELLER_FONTS } from '@designdeck/shared';

/**
 * Font Configuration
 *
 * Centralized font definitions for the Seller Creator's text tool.
 * Every face offered here is self-hosted via @fontsource (imported in
 * main.tsx): embedding shops' CSP forbids requests to Google Fonts, and
 * fonts.test.ts fails when a face lacks its import.
 */

export interface FontDefinition {
  /** Display name shown in UI */
  name: string;
  /** CSS font-family value */
  value: string;
  /** Font category for filtering */
  category: 'sans-serif' | 'serif' | 'display' | 'handwriting' | 'monospace';
  /**
   * Whether the family ships italic faces. Without them (Oswald) the
   * browser slants the upright face when the italic toggle is on.
   */
  hasItalic: boolean;
}

/**
 * Weights the text tool offers: its bold toggle switches between
 * fontWeight 'normal' (400) and 'bold' (700).
 */
export const FONT_WEIGHTS = [400, 700] as const;

/**
 * Available fonts for text editing: the seller font list shared with the
 * API, which renders the print file from the same families (see
 * SELLER_FONTS in @designdeck/shared). A family is added there, together
 * with its TTFs in the API and its @fontsource imports in main.tsx (every
 * FONT_WEIGHTS weight, plus the italic variant when the family has one),
 * never only here.
 */
export const FONTS: FontDefinition[] = SELLER_FONTS.map((font) => ({
  name: font.name,
  value: font.value,
  category: font.category,
  hasItalic: font.italic,
}));

/**
 * Default font for new text objects
 */
export const DEFAULT_FONT = FONTS[0];

/**
 * Get font definition by value
 */
export function getFontByValue(value: string): FontDefinition | undefined {
  return FONTS.find((f) => f.value === value);
}

/**
 * Get fonts by category
 */
export function getFontsByCategory(
  category: FontDefinition['category']
): FontDefinition[] {
  return FONTS.filter((f) => f.category === category);
}

/**
 * Simplified font list for compact UI (TextPropertiesBar)
 * Uses a subset of the most popular fonts
 */
export const FONTS_COMPACT: FontDefinition[] = FONTS.slice(0, 7);
